import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Car,
  Shield,
  AlertTriangle,
  CheckCircle,
  Clock,
  DollarSign,
  User,
  MapPin,
  Calendar,
  Wrench,
  Check,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Separator } from "../components/ui/separator";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  new: { label: "Submitted", class: "bg-blue-50 text-blue-700 border-blue-200" },
  under_review: { label: "Under Review", class: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  vin_verification: { label: "Verification", class: "bg-purple-50 text-purple-700 border-purple-200" },
  valuation_calculated: { label: "Valuation Complete", class: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  approved: { label: "Approved", class: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  escalated: { label: "Under Review", class: "bg-amber-50 text-amber-700 border-amber-200" },
  closed: { label: "Complete", class: "bg-slate-100 text-slate-600 border-slate-200" },
};

const severityConfig = {
  minor_cosmetic: { label: "Minor Cosmetic", class: "text-emerald-600" },
  moderate_structural: { label: "Moderate Structural", class: "text-amber-600" },
  major_structural: { label: "Major Structural", class: "text-rose-600" },
};

export default function ClaimDetail() {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClaim();
  }, [claimId]);

  const fetchClaim = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/claims/${claimId}`);
      setClaim(res.data);
    } catch (error) {
      console.error("Error fetching claim:", error);
      toast.error("Failed to load claim details");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return "$0";
    const num = Number(value);
    const hasDecimals = num % 1 !== 0;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: hasDecimals ? 2 : 0,
    }).format(num);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="claim-detail-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Skeleton className="h-64" />
            <Skeleton className="h-96" />
          </div>
          <div className="space-y-6">
            <Skeleton className="h-64" />
            <Skeleton className="h-48" />
          </div>
        </div>
      </div>
    );
  }

  if (!claim) {
    return (
      <div className="text-center py-12" data-testid="claim-not-found">
        <AlertTriangle className="w-12 h-12 text-slate-600 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-900">Claim Not Found</h2>
        <p className="text-slate-600 mt-2 max-w-md mx-auto">We couldn't locate a claim with the provided details. Please verify your claim number and try again.</p>
        <div className="flex gap-3 justify-center mt-6">
          <Button variant="outline" className="border-slate-200" onClick={() => navigate("/")}>
            Search Again
          </Button>
          <Button className="bg-[#2563EB] hover:bg-[#1D4ED8]" onClick={() => navigate("/file-claim")}>
            File a New Claim
          </Button>
        </div>
      </div>
    );
  }

  const dvData = [
    { name: "Pre-Accident", value: claim.dv_calculation?.pre_accident_value || 0, fill: "#2563EB" },
    { name: "Post-Repair", value: claim.dv_calculation?.post_repair_value || 0, fill: "#94A3B8" },
    { name: "DV Loss", value: claim.dv_calculation?.estimated_dv_loss || 0, fill: "#EF4444" },
  ];

  return (
    <div className="space-y-6" data-testid="claim-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/")}
            className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">
                {claim.claim_id}
              </h1>
              <Badge variant="outline" className={statusConfig[claim.status]?.class}>
                {statusConfig[claim.status]?.label || claim.status}
              </Badge>
            </div>
            <p className="text-sm text-slate-600 mt-1">
              Submitted {formatDateTime(claim.created_at)}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Claim Summary */}
          <div className="dashboard-card">
            <div className="card-header">
              <h3 className="card-title flex items-center gap-2">
                <User className="w-5 h-5 text-[#2563EB]" />
                Claim Summary
              </h3>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <p className="text-xs text-slate-600 uppercase tracking-wide">Policyholder</p>
                  <p className="font-medium text-slate-900 mt-1">{claim.policyholder_name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-600 uppercase tracking-wide">State</p>
                  <p className="font-medium text-slate-900 mt-1 flex items-center gap-1">
                    <MapPin className="w-4 h-4 text-slate-600" />
                    {claim.state}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-600 uppercase tracking-wide">Accident Date</p>
                  <p className="font-medium text-slate-900 mt-1 flex items-center gap-1">
                    <Calendar className="w-4 h-4 text-slate-600" />
                    {formatDate(claim.accident_date)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-600 uppercase tracking-wide">Repair Completed</p>
                  <p className="font-medium text-slate-900 mt-1 flex items-center gap-1">
                    <Wrench className="w-4 h-4 text-slate-600" />
                    {formatDate(claim.repair_completion_date)}
                  </p>
                </div>
              </div>
              <Separator className="my-6" />
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-xs text-slate-600 uppercase tracking-wide">Repair Severity</p>
                  <p className={`font-medium mt-1 ${severityConfig[claim.repair_severity]?.class}`}>
                    {severityConfig[claim.repair_severity]?.label || claim.repair_severity}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-600 uppercase tracking-wide">Assigned Analyst</p>
                  <p className="font-medium text-slate-900 mt-1">{claim.assigned_analyst}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-600 uppercase tracking-wide">Dealer Source</p>
                  <p className="font-medium text-slate-900 mt-1">{claim.dealer_source || "Direct"}</p>
                </div>
              </div>
            </div>
          </div>

          {/* VIN Intelligence Panel */}
          <div className="dashboard-card">
            <div className="card-header">
              <h3 className="card-title flex items-center gap-2">
                <Car className="w-5 h-5 text-[#2563EB]" />
                VIN Intelligence
              </h3>
            </div>
            <div className="card-body">
              <div className="flex items-center gap-4 mb-6">
                <span className="vin-display text-lg">{claim.vin}</span>
                <Badge variant="outline" className="bg-slate-50 border-slate-200 text-slate-700">
                  {claim.vehicle_year_make_model}
                </Badge>
              </div>

              {claim.vin_data && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Vehicle Profile */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900 mb-4">Vehicle Profile</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Year</span>
                        <span className="text-sm font-medium text-slate-900">{claim.vin_data.vehicle_profile.year}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Make</span>
                        <span className="text-sm font-medium text-slate-900">{claim.vin_data.vehicle_profile.make}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Model</span>
                        <span className="text-sm font-medium text-slate-900">{claim.vin_data.vehicle_profile.model}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Trim</span>
                        <span className="text-sm font-medium text-slate-900">{claim.vin_data.vehicle_profile.trim}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">MSRP</span>
                        <span className="text-sm font-medium text-slate-900">{formatCurrency(claim.vin_data.vehicle_profile.msrp)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Current Market Value</span>
                        <span className="text-sm font-medium text-[#2563EB]">
                          {formatCurrency(claim.vin_data.vehicle_profile.current_market_value)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Est. Mileage</span>
                        <span className="text-sm font-medium text-slate-900">
                          {claim.vin_data.vehicle_profile.mileage_estimate?.toLocaleString()} mi
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Risk Indicators */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900 mb-4">Risk Indicators</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">Luxury Segment</span>
                        <Badge variant="outline" className={claim.vin_data.risk_indicators.luxury_segment ? "bg-purple-50 text-purple-700 border-purple-200" : "bg-slate-50 text-slate-600 border-slate-200"}>
                          {claim.vin_data.risk_indicators.luxury_segment ? "Yes" : "No"}
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">ADAS Complexity</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[#2563EB] rounded-full"
                              style={{ width: `${claim.vin_data.risk_indicators.adas_complexity_score * 10}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-slate-900 w-6">{claim.vin_data.risk_indicators.adas_complexity_score}</span>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">Accident Sensitivity</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-amber-500 rounded-full"
                              style={{ width: `${claim.vin_data.risk_indicators.accident_sensitivity_score * 10}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-slate-900 w-6">{claim.vin_data.risk_indicators.accident_sensitivity_score}</span>
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Depreciation Curve</span>
                        <Badge variant="outline" className="capitalize bg-slate-50 border-slate-200 text-slate-700">
                          {claim.vin_data.risk_indicators.depreciation_curve}
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">DV Susceptibility</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-rose-500 rounded-full"
                              style={{ width: `${claim.vin_data.risk_indicators.historical_dv_susceptibility}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-slate-900 w-8">{claim.vin_data.risk_indicators.historical_dv_susceptibility}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* DV Calculation Engine */}
          <div className="dashboard-card">
            <div className="card-header">
              <h3 className="card-title flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#2563EB]" />
                Diminished Value Calculation
              </h3>
            </div>
            <div className="card-body">
              {claim.dv_calculation && (
                <>
                  {/* Modern Progress Bar Visualization */}
                  <div className="space-y-6 mb-8">
                    {/* Pre-Accident Value */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-base font-medium text-slate-900">Pre-Accident Value</span>
                        <span className="text-lg font-bold text-slate-900">{formatCurrency(claim.dv_calculation.pre_accident_value)}</span>
                      </div>
                      <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-[#2563EB] rounded-full" style={{ width: '100%' }} />
                      </div>
                    </div>

                    {/* Post-Repair Value */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-base font-medium text-slate-900">Post-Repair Value</span>
                        <span className="text-lg font-bold text-slate-900">{formatCurrency(claim.dv_calculation.post_repair_value)}</span>
                      </div>
                      <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-slate-500 rounded-full" 
                          style={{ width: `${(claim.dv_calculation.post_repair_value / claim.dv_calculation.pre_accident_value) * 100}%` }} 
                        />
                      </div>
                    </div>

                    {/* LossPay Covers (Eligible Payout) */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-base font-medium text-[#2563EB]">LossPay Covers</span>
                        <span className="text-lg font-bold text-[#2563EB]">{formatCurrency(claim.dv_calculation.eligible_payout)}</span>
                      </div>
                      <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full" 
                          style={{ 
                            width: `${(claim.dv_calculation.eligible_payout / claim.dv_calculation.pre_accident_value) * 100}%`,
                            background: 'linear-gradient(90deg, #2563EB 0%, #60A5FA 100%)'
                          }} 
                        />
                      </div>
                    </div>

                    {/* Recovery Percentage */}
                    <p className="text-sm text-slate-600 mt-4">
                      ≈ {Math.round((claim.dv_calculation.eligible_payout / claim.dv_calculation.estimated_dv_loss) * 100)}% of diminished value recovered
                    </p>
                  </div>

                  <Separator className="my-6" />

                  <div className="flex flex-wrap gap-6 text-sm">
                    <div>
                      <span className="text-slate-600">Coverage Limit:</span>
                      <span className="font-medium text-slate-900 ml-2">{formatCurrency(claim.dv_calculation.coverage_limit)}</span>
                    </div>
                    <div>
                      <span className="text-slate-600">Deductible:</span>
                      <span className="font-medium text-slate-900 ml-2">{formatCurrency(claim.dv_calculation.deductible)}</span>
                    </div>
                    <div>
                      <span className="text-slate-600">Estimated DV Loss:</span>
                      <span className="font-medium text-rose-600 ml-2">{formatCurrency(claim.dv_calculation.estimated_dv_loss)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Claim Status Info */}
          <div className="dashboard-card">
            <div className="card-header">
              <h3 className="card-title flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#2563EB]" />
                Claim Status
              </h3>
            </div>
            <div className="card-body">
              <div className="text-center py-4">
                {claim.status === "approved" || claim.status === "closed" ? (
                  <>
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-emerald-600" />
                    </div>
                    <p className="font-semibold text-emerald-700 text-lg">Claim Approved</p>
                    <p className="text-sm text-slate-600 mt-1">Your compensation has been processed</p>
                  </>
                ) : claim.status === "escalated" ? (
                  <>
                    <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Clock className="w-8 h-8 text-amber-600" />
                    </div>
                    <p className="font-semibold text-amber-700 text-lg">Additional Review Required</p>
                    <p className="text-sm text-slate-600 mt-1">Our team is reviewing additional details</p>
                  </>
                ) : (
                  <>
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Clock className="w-8 h-8 text-[#2563EB]" />
                    </div>
                    <p className="font-semibold text-slate-900 text-lg">Processing Your Claim</p>
                    <p className="text-sm text-slate-600 mt-1">We're working on your claim</p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="dashboard-card">
            <div className="card-header">
              <h3 className="card-title flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#2563EB]" />
                Claims Timeline
              </h3>
            </div>
            <div className="card-body">
              <ul className="claim-timeline">
                {claim.timeline?.map((event, idx) => (
                  <li key={idx} className="timeline-event" data-testid={`timeline-event-${idx}`}>
                    <div className={`timeline-marker ${event.status}`}>
                      {event.status === "completed" && <Check className="w-3 h-3" />}
                      {event.status === "in_progress" && <Clock className="w-3 h-3" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{event.description}</p>
                      <p className="text-xs text-slate-600 mt-1">{formatDateTime(event.timestamp)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Payout Summary (if approved) */}
          {claim.payout_amount && (
            <div className="dashboard-card border-emerald-200 bg-emerald-50/30">
              <div className="card-body">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-emerald-700 font-medium">Claim Approved</p>
                    <p className="text-xs text-emerald-600">Payment authorized</p>
                  </div>
                </div>
                <div className="text-center py-4">
                  <p className="text-xs text-slate-600 uppercase tracking-wide">Payout Amount</p>
                  <p className="text-3xl font-bold text-emerald-700 mt-1">
                    {formatCurrency(claim.payout_amount)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
